import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {JSONObject, JSONValue} from 'util/Types';

import {getConfigShapeAtConfigShapePath} from '../step-config-settings/StepConfigSettingsUtils';
import {useStepConfigEditorContext} from '../StepConfigEditorContext';
import {
  BANNER_KEY,
  EXPANDABLE_DETAILS_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  INFO_TEXT_UI_SCHEMA_KEY,
  IS_COLLAPSIBLE_SCHEMA_KEY,
  isShapeUISchemaFieldComponentValue,
  isShapeUISchemaNestedShapeValues,
  NUM_COLUMNS_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  ShapeUISchema,
  ConfigShapePath,
  SHOW_DIVIDER_LINE_KEY,
  ON_CHANGE_CONFIG_MIDDLEWARE_KEY,
  isFieldUISchemaWithStandardComponent,
  FieldUISchemaBanner,
} from '../StepConfigEditorTypes';
import {getSectionNameOrNull} from './SectionName';
import StepConfigEditorBanner from './StepConfigEditorBanner';
import StepConfigEditorRouter from './StepConfigEditorRouter';
import StepConfigFieldsTemplate, {CollapsingSection} from './StepConfigFieldsTemplate';

export interface ShapeEditorProps {
  config: JSONObject;
  configShape: PipelineConfigJsonSpec;
  onChangeConfig: (config: JSONValue, path: ConfigShapePath, newValue: JSONValue) => void;
  disabled?: boolean;
  uiSchema: ShapeUISchema;
}

/** @package */
export default function ShapeEditor({
  config,
  configShape,
  onChangeConfig,
  disabled,
  uiSchema,
}: ShapeEditorProps): JSX.Element {
  const {dataFramesInfos} = useStepConfigEditorContext();

  function handleChangeConfig(config: JSONObject, path: ConfigShapePath, newValue: JSONValue): void {
    if (uiSchema?.[ON_CHANGE_CONFIG_MIDDLEWARE_KEY]) {
      onChangeConfig(
        uiSchema[ON_CHANGE_CONFIG_MIDDLEWARE_KEY](newValue, path, config, dataFramesInfos),
        path,
        newValue
      );
    } else {
      onChangeConfig(config, path, newValue);
    }
  }

  function shouldDisplayBanner(banner: FieldUISchemaBanner): boolean {
    if (banner.visibilityFn == null) {
      return true; // If no visibility function, always show
    }
    return banner.visibilityFn(config);
  }

  const fieldOrder = uiSchema[FIELD_ORDER_UI_SCHEMA_KEY];
  const sectionName = uiSchema[SECTION_NAME_UI_SCHEMA_KEY];
  const sectionInfoText = uiSchema?.[INFO_TEXT_UI_SCHEMA_KEY];
  const isCollapsible = uiSchema?.[IS_COLLAPSIBLE_SCHEMA_KEY];
  const numColumns = uiSchema?.[NUM_COLUMNS_KEY];

  if (fieldOrder.length === 0) {
    return <></>;
  }

  if (isShapeUISchemaNestedShapeValues(uiSchema)) {
    assert(numColumns == null, 'Num columns not supported with nested shapes');
    const sectionName = uiSchema[SECTION_NAME_UI_SCHEMA_KEY];
    const sectionInfoText = uiSchema?.[INFO_TEXT_UI_SCHEMA_KEY];
    const sectionNameOrNull = getSectionNameOrNull({sectionName, sectionInfoText});

    const fieldElements = fieldOrder.map((variableName) => (
      <StepConfigEditorRouter
        key={variableName}
        config={config}
        configShape={configShape}
        dataTestAttrs={{key: variableName}}
        onChangeConfig={handleChangeConfig}
        disabled={disabled}
        uiSchema={uiSchema[variableName]}
      />
    ));

    if (isCollapsible) {
      return <CollapsingSection sectionHeader={sectionNameOrNull}>{fieldElements}</CollapsingSection>;
    }

    const banner = uiSchema[BANNER_KEY];
    const showBanner = banner && shouldDisplayBanner(banner);
    const bannerPlacement = banner?.placement ?? 'above';

    return (
      <>
        {showBanner && bannerPlacement === 'above' && <StepConfigEditorBanner {...banner} />}
        {sectionNameOrNull}
        {showBanner && bannerPlacement === 'below' && <StepConfigEditorBanner {...banner} />}
        {fieldElements}
      </>
    );
  }
  assert(isShapeUISchemaFieldComponentValue(uiSchema), 'uiSchema must be a ShapeUISchemaFieldComponentValue');
  const expandableDetails = uiSchema?.[EXPANDABLE_DETAILS_KEY];

  const fields = fieldOrder.map((variableName) => {
    const field = uiSchema[variableName];
    const isExpandable = variableName === expandableDetails?.headerField;
    assert(field != null, `Field ${variableName} is not defined in uiSchema`);

    const currentConfigShape = isFieldUISchemaWithStandardComponent(field)
      ? getConfigShapeAtConfigShapePath(field.configShapePath, configShape, config)
      : undefined;

    // Extract banner from field if it exists and should be displayed
    const fieldBanner =
      isFieldUISchemaWithStandardComponent(field) &&
      field[BANNER_KEY] &&
      shouldDisplayBanner(field[BANNER_KEY]) ? (
        <StepConfigEditorBanner {...field[BANNER_KEY]} />
      ) : undefined;

    return {
      systemVariableName: variableName,
      // eslint-disable-next-line benchlint/no-in-operator
      variableName: 'displayName' in field ? field.displayName : undefined,
      // eslint-disable-next-line benchlint/no-in-operator
      infoText: 'infoText' in field ? field.infoText : undefined,
      required: currentConfigShape?.required,
      banner: fieldBanner,
      editor: (
        <StepConfigEditorRouter
          key={variableName}
          config={config}
          configShape={configShape}
          dataTestAttrs={{key: variableName}}
          onChangeConfig={handleChangeConfig}
          disabled={disabled}
          uiSchema={uiSchema[variableName]}
          isExpandable={isExpandable}
        />
      ),
    };
  });

  const banner = uiSchema[BANNER_KEY];
  const showBanner = banner && shouldDisplayBanner(banner);
  const bannerPlacement = banner?.placement ?? 'above';

  return (
    <>
      {showBanner && bannerPlacement === 'above' && <StepConfigEditorBanner {...banner} />}
      <StepConfigFieldsTemplate
        numColumns={numColumns ?? 1}
        fields={fields}
        sectionName={sectionName}
        isCollapsible={isCollapsible}
        sectionInfoText={sectionInfoText}
        isCompact={true}
        expandableDetails={expandableDetails}
        banner={
          showBanner && bannerPlacement === 'below' ? <StepConfigEditorBanner {...banner} /> : undefined
        }
      />
      {uiSchema[SHOW_DIVIDER_LINE_KEY] && <hr className={css(styles.dividerLine)} />}
    </>
  );
}

const styles = StyleSheet.create({
  dividerLine: {
    marginTop: 4,
    marginBottom: 10,
    borderTop: `1px solid ${GRAY_LIGHT}`,
    width: '100%',
  },
});
